/** Client-side forward geocoding via Mapbox (public token, Bay Area–biased). */

/**
 * Whether a listing's coordinate can be honestly plotted on a map.
 *
 * Rejects the two ways a feed lies about location: a non-finite/missing value,
 * and the "null island" sentinel — a 0 in either axis. No Bay Area home sits on
 * the equator or prime meridian, and an exact (0,0) plots in the Atlantic off
 * West Africa (the failure iOS hit). Also rejects anything outside valid WGS84
 * range. Legitimately remote-but-real California coords (Tahoe, Monterey,
 * Mendocino) pass — only bad data is dropped, never a real home.
 */
export function isPlottableCoord(
  lat: number | null | undefined,
  lon: number | null | undefined
): boolean {
  if (typeof lat !== 'number' || typeof lon !== 'number') return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (lat === 0 || lon === 0) return false;
  if (lat < -90 || lat > 90) return false;
  if (lon < -180 || lon > 180) return false;
  return true;
}

export interface GeocodeResult {
  lat: number;
  lng: number;
  placeName: string;
}

export async function geocodeAddress(query: string): Promise<GeocodeResult | null> {
  const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
  if (!token || !query.trim()) return null;

  const url =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query.trim())}.json` +
    `?access_token=${token}&limit=1&country=us&proximity=-122.1,37.5&types=address,poi,place,postcode,neighborhood`;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      features?: Array<{ center?: [number, number]; place_name?: string }>;
    };
    const feature = data.features?.[0];
    if (!feature?.center) return null;
    return {
      lng: feature.center[0],
      lat: feature.center[1],
      placeName: feature.place_name ?? query.trim(),
    };
  } catch {
    return null;
  }
}
